import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  $getNodeByKey,
  $getSelection,
  $isElementNode,
  $isNodeSelection,
  $isTextNode,
  $isDecoratorNode,
  $setSelection,
  BLUR_COMMAND,
  COMMAND_PRIORITY_LOW,
  KEY_ARROW_LEFT_COMMAND,
  KEY_ARROW_RIGHT_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND
} from 'lexical';
import { mergeRegister } from '@lexical/utils';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';

import { Tooltip } from '../Tooltip';
// eslint-disable-next-line import/no-cycle
import { $isTermTranslationNode } from './TermTranslationNode';
import { positionCursorAroundSpecialNode } from '../../../utils/lexicalUtils';

const UnderlineText = styled.span`
  text-decoration: underline;
  position: relative;

  /* Expand clickable area for better UX, but keep it conservative */
  padding: 1px 2px;
  margin: -1px -2px;

  /* Add pseudo-elements for click-through areas at the edges */
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 15px;
    pointer-events: none;
    z-index: 1;
  }

  &::before {
    left: -15px;
  }

  &::after {
    right: -15px;
  }

  ${({ $isSelected }) =>
    $isSelected &&
    `
    border-radius: 4px;
    // color: var(--primary-default);
    // background-color: var(--wash-orange-hover);
    box-shadow:  0 0 0 2px var(--white), 0 0 0 3px var(--primary-hover);
  `}
`;

const WrapText = styled.p`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 260px;
`;

const TermTranslationComponent = ({ nodeKey, data, value }) => {
  const ref = useRef(null);
  const [editor] = useLexicalComposerContext();
  const [isSelected] = useLexicalNodeSelection(nodeKey);

  const handleTermClick = useCallback(
    (event) => {
      // Use the same click boundary pattern as EntityNode for consistency
      const rect = ref.current.getBoundingClientRect();
      const clickX = event.clientX;
      const elementWidth = rect.width;
      const leftBoundary = rect.left + elementWidth * 0.2; // 20% from left
      const rightBoundary = rect.left + elementWidth * 0.8; // 20% from right

      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (node) {
          if (clickX < leftBoundary) {
            // Click in left 20% - position cursor before
            positionCursorAroundSpecialNode(editor, node, 'before', nodeKey);
          } else if (clickX > rightBoundary) {
            // Click in right 20% - position cursor after
            positionCursorAroundSpecialNode(editor, node, 'after', nodeKey);
          } else {
            // Click in center 60% - position cursor after (default behavior for terms)
            positionCursorAroundSpecialNode(editor, node, 'after', nodeKey);
          }
        }
      });
    },
    [editor, nodeKey]
  );

  const onBlur = useCallback(() => {
    const node = $getNodeByKey(nodeKey);
    if (!node?.isSelected()) {
      return false;
    }

    const selection = $getSelection();
    if (!$isNodeSelection(selection)) {
      return false;
    }

    $setSelection(null);
    return false;
  }, [nodeKey]);

  const onDelete = useCallback(
    (payload) => {
      if (isSelected && $isNodeSelection($getSelection())) {
        payload.preventDefault();
        const node = $getNodeByKey(nodeKey);
        if ($isTermTranslationNode(node)) {
          node.remove();
        }
      }
      return false;
    },
    [isSelected, nodeKey]
  );

  const onArrowLeftPress = useCallback(
    (event) => {
      const node = $getNodeByKey(nodeKey);
      if (!node?.isSelected()) {
        return false;
      }
      let handled = false;
      const nodeToSelect = node.getPreviousSibling();
      if ($isElementNode(nodeToSelect)) {
        nodeToSelect.selectEnd();
        handled = true;
      }
      if ($isTextNode(nodeToSelect)) {
        nodeToSelect.select();
        handled = true;
      }
      if ($isDecoratorNode(nodeToSelect)) {
        nodeToSelect.selectNext();
        handled = true;
      }
      if (nodeToSelect === null) {
        node.selectPrevious();
        handled = true;
      }
      if (handled) {
        event.preventDefault();
      }
      return handled;
    },
    [nodeKey]
  );

  const onArrowRightPress = useCallback(
    (event) => {
      const node = $getNodeByKey(nodeKey);
      if (!node?.isSelected()) {
        return false;
      }
      let handled = false;
      const nodeToSelect = node.getNextSibling();
      if ($isElementNode(nodeToSelect)) {
        nodeToSelect.selectStart();
        handled = true;
      }
      if ($isTextNode(nodeToSelect)) {
        nodeToSelect.select(0, 0);
        handled = true;
      }
      if ($isDecoratorNode(nodeToSelect)) {
        nodeToSelect.selectPrevious();
        handled = true;
      }
      if (nodeToSelect === null) {
        node.selectNext();
        handled = true;
      }
      if (handled) {
        event.preventDefault();
      }
      return handled;
    },
    [nodeKey]
  );

  useEffect(() => {
    const unregister = mergeRegister(
      editor.registerCommand(KEY_DELETE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_BACKSPACE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ARROW_LEFT_COMMAND, onArrowLeftPress, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ARROW_RIGHT_COMMAND, onArrowRightPress, COMMAND_PRIORITY_LOW),
      editor.registerCommand(BLUR_COMMAND, onBlur, COMMAND_PRIORITY_LOW)
    );
    return () => {
      unregister();
    };
  }, [editor, onArrowLeftPress, onArrowRightPress, onDelete, onBlur]);

  return (
    <Tooltip content={<WrapText>{data.definition}</WrapText>} position='top'>
      <UnderlineText ref={ref} $isSelected={isSelected} onClick={handleTermClick}>
        {value.toLowerCase()}
      </UnderlineText>
    </Tooltip>
  );
};

TermTranslationComponent.propTypes = {
  nodeKey: PropTypes.string.isRequired,
  data: PropTypes.shape({
    definition: PropTypes.string.isRequired
  }).isRequired,
  value: PropTypes.string.isRequired
};

export { TermTranslationComponent };
